import Vue from 'vue';
import types from '../../mutation-types';
import getters, { getContactViewSelectedChatConversation } from './getters';
import actions from './actions';
import { findPendingMessageIndex } from './helpers';
import { MESSAGE_STATUS } from 'shared/constants/messages';
import wootConstants from 'dashboard/constants/globals';
import { BUS_EVENTS } from '../../../../shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';

const state = {
  allContactViewConversations: [],
  listLoadingStatus: true,
  chatStatusFilter: wootConstants.STATUS_TYPE.ALL,
  chatSortFilter: wootConstants.SORT_BY_TYPE.LATEST,
  currentInbox: null,
  selectedChatId: null,
  selectedContactId: null,
  appliedFilters: [],
  contextMenuChatId: null,
  conversationParticipants: [],
  conversationLastSeen: null,
  syncConversationsMessages: {},
  conversationFilters: {},
};

// mutations
export const mutations = {
  [types.CONTACT_SET_ALL_CONVERSATION](_state, conversationList) {
    const newAllConversations = [..._state.allContactViewConversations];
    conversationList.forEach(conversation => {
      const indexInCurrentList = newAllConversations.findIndex(
        c => c.id === conversation.id
      );
      if (indexInCurrentList < 0) {
        newAllConversations.push(conversation);
      } else if (conversation.id !== _state.selectedChatId) {
        // If the conversation is already in the list, replace it
        // Added this to fix the issue of the conversation not being updated
        // When reconnecting to the websocket. If the selectedChatId is not the same as
        // the conversation.id in the store, replace the existing conversation with the new one
        newAllConversations[indexInCurrentList] = conversation;
      } else {
        // If the conversation is already in the list and selectedChatId is the same,
        // replace all data except the messages array, attachments, dataFetched, allMessagesLoaded
        const existingConversation = newAllConversations[indexInCurrentList];
        newAllConversations[indexInCurrentList] = {
          ...conversation,
          allMessagesLoaded: existingConversation.allMessagesLoaded,
          messages: existingConversation.messages,
          dataFetched: existingConversation.dataFetched,
          attachments: existingConversation.attachments,
        };
      }
    });
    _state.allContactViewConversations = newAllConversations;
  },
  [types.CONTACT_EMPTY_ALL_CONVERSATION](_state) {
    _state.allContactViewConversations = [];
    _state.selectedChatId = null;
    _state.selectedContactId = null;
  },
  [types.CONTACT_SET_ALL_MESSAGES_LOADED](_state) {
    const [chat] = getContactViewSelectedChatConversation(_state);
    Vue.set(chat, 'allMessagesLoaded', true);
  },

  [types.CONTACT_CLEAR_ALL_MESSAGES_LOADED](_state) {
    const [chat] = getContactViewSelectedChatConversation(_state);
    Vue.set(chat, 'allMessagesLoaded', false);
  },
  [types.CONTACT_CLEAR_CURRENT_CHAT_WINDOW](_state) {
    _state.selectedChatId = null;
    _state.selectedContactId = null;
  },

  [types.CONTACT_SET_PREVIOUS_CONVERSATIONS](_state, { id, data, contactId }) {
    if (data.length && contactId) {
      const conversations = _state.allContactViewConversations.filter(
        c => c.meta?.sender?.id == contactId
      );
      if (conversations.length) {
        const [chat] = conversations;
        if (!chat.messages) {
          chat.messages = [];
        }
        const existingIds = new Set(chat.messages.map(m => m.id));
        const newMessages = data.filter(m => !existingIds.has(m.id));
        chat.messages.unshift(...newMessages);
      }
    }
  },
  [types.CONTACT_SET_ALL_ATTACHMENTS](_state, { id, data }) {
    const [chat] = _state.allContactViewConversations.filter(c => c.id === id);
    if (!chat) return;
    Vue.set(chat, 'attachments', []);
    chat.attachments.push(...data);
  },
  [types.SET_MISSING_MESSAGES](_state, { id, data }) {
    const [chat] = _state.allContactViewConversations.filter(c => c.id === id);
    if (!chat) return;
    Vue.set(chat, 'messages', data);
  },

  [types.CONTACT_SET_CURRENT_CHAT_WINDOW](_state, activeChat) {
    if (activeChat) {
      _state.selectedChatId = activeChat.id;
      _state.selectedContactId = activeChat.meta?.sender?.id;
    }
  },

  [types.CONTACT_ASSIGN_AGENT](_state, assignee) {
    const [chat] = getContactViewSelectedChatConversation(_state);
    Vue.set(chat.meta, 'assignee', assignee);
  },

  [types.CONTACT_ASSIGN_TEAM](_state, { team, conversationId }) {
    const [chat] = _state.allContactViewConversations.filter(c => c.id === conversationId);
    Vue.set(chat.meta, 'team', team);
  },

  [types.CONTACT_UPDATE_CONVERSATION_LAST_ACTIVITY](
    _state,
    { lastActivityAt, conversationId }
  ) {
    const [chat] = _state.allContactViewConversations.filter(c => c.id === conversationId);
    if (chat) {
      Vue.set(chat, 'last_activity_at', lastActivityAt);
    }
  },
  [types.CONTACT_ASSIGN_PRIORITY](_state, { priority, conversationId }) {
    const [chat] = _state.allContactViewConversations.filter(c => c.id === conversationId);
    Vue.set(chat, 'priority', priority);
  },

  [types.CONTACT_UPDATE_CONVERSATION_CUSTOM_ATTRIBUTES](_state, payload) {
    let customAttributes;
    let conversationId;
    if (
      payload &&
      typeof payload === 'object' &&
      'conversationId' in payload &&
      'customAttributes' in payload
    ) {
      ({ conversationId, customAttributes } = payload);
    } else {
      customAttributes = payload;
    }
    const conversation =
      conversationId != null
        ? getters.getConversationById(_state)(conversationId)
        : getContactViewSelectedChatConversation(_state)[0];
    if (!conversation) return;
    Vue.set(conversation, 'custom_attributes', customAttributes);
  },

  [types.CONTACT_CHANGE_CONVERSATION_STATUS](
    _state,
    { conversationId, status, snoozedUntil }
  ) {
    const conversation =
      getters.getConversationById(_state)(conversationId) || {};
    Vue.set(conversation, 'snoozed_until', snoozedUntil);
    Vue.set(conversation, 'status', status);
  },

  [types.CONTACT_MUTE_CONVERSATION](_state) {
    const [chat] = getContactViewSelectedChatConversation(_state);
    Vue.set(chat, 'muted', true);
  },

  [types.CONTACT_UNMUTE_CONVERSATION](_state) {
    const [chat] = getContactViewSelectedChatConversation(_state);
    Vue.set(chat, 'muted', false);
  },

  [types.CONTACT_ADD_CONVERSATION_ATTACHMENTS]({ allContactViewConversations }, message) {
    const { conversation_id: conversationId } = message;
    const [chat] = getContactViewSelectedChatConversation({
      allContactViewConversations,
      selectedChatId: conversationId,
    });

    if (!chat) return;
    if (!Array.isArray(chat.attachments)) {
      Vue.set(chat, 'attachments', []);
    }

    const isMessageSent =
      message.status === MESSAGE_STATUS.SENT && message.attachments;
    if (isMessageSent) {
      message.attachments.forEach(attachment => {
        if (!chat.attachments.some(a => a.id === attachment.id)) {
          chat.attachments.push(attachment);
        }
      });
    }
  },

  [types.CONTACT_DELETE_CONVERSATION_ATTACHMENTS]({ allContactViewConversations }, message) {
    const { conversation_id: conversationId } = message;
    const [chat] = getContactViewSelectedChatConversation({
      allContactViewConversations,
      selectedChatId: conversationId,
    });

    if (!chat) return;
    if (!Array.isArray(chat.attachments)) {
      Vue.set(chat, 'attachments', []);
    }

    const isMessageSent = message.status === MESSAGE_STATUS.SENT;
    if (isMessageSent) {
      const attachmentIndex = chat.attachments.findIndex(
        a => a.message_id === message.id
      );
      if (attachmentIndex !== -1) chat.attachments.splice(attachmentIndex, 1);
    }
  },

  [types.CONTACT_ADD_MESSAGE]({ allContactViewConversations, selectedChatId }, message) {
    const { conversation_id: conversationId } = message;
    const [chat] = getContactViewSelectedChatConversation({
      allContactViewConversations,
      selectedChatId: conversationId,
    });
    if (!chat) return;

    const pendingMessageIndex = findPendingMessageIndex(chat, message);
    if (pendingMessageIndex !== -1) {
      Vue.set(chat.messages, pendingMessageIndex, message);
    } else {
      chat.messages.push(message);
      chat.timestamp = message.created_at;
      const { conversation: { unread_count: unreadCount = 0 } = {} } = message;
      chat.unread_count = unreadCount;
      if (selectedChatId === conversationId) {
        emitter.emit(BUS_EVENTS.FETCH_LABEL_SUGGESTIONS);
        emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
      }
    }
  },

  [types.CONTACT_ADD_CONVERSATION](_state, conversation) {
    // Buscar si ya existe una conversación con el mismo senderId
    const existingConversationIndex = _state.allContactViewConversations.findIndex(
      c => c.meta?.sender?.id == conversation.meta?.sender?.id
    );

    if (existingConversationIndex !== -1) {
      // Si existe, actualizar la conversación existente
      const existingConversation = _state.allContactViewConversations[existingConversationIndex];

      // Asegurarse de que la propiedad messages exista
      if (!existingConversation.messages) {
        existingConversation.messages = [];
      }

      if (_state.selectedChatId) {
        const selectedChat = _state.allContactViewConversations.find(
          c => c.id == _state.selectedChatId
        );

        if (selectedChat?.meta?.sender?.id == conversation.meta?.sender?.id) {
          _state.selectedChatId = conversation.id;
        }
      }

      const updatedMessages = [
        ...(existingConversation.messages || []),
        ...(conversation.messages || []),
      ];

      // Preserve existing sender labels if not provided in the new conversation
      const existingLabels = existingConversation.meta?.sender?.labels || [];
      const newSenderLabels = conversation.meta?.sender?.labels;

      const updatedConversation = {
        ...existingConversation,
        ...conversation,
        messages: updatedMessages,
      };

      // If the new conversation doesn't include labels, preserve the existing ones
      if (updatedConversation.meta?.sender && !newSenderLabels) {
        updatedConversation.meta.sender.labels = existingLabels;
      }

      Vue.set(_state.allContactViewConversations, existingConversationIndex, updatedConversation);
    } else {
      // Si no existe, agregar la nueva conversación
      _state.allContactViewConversations.push(conversation);
    }
  },

  [types.CONTACT_UPDATE_CONVERSATION](_state, conversation) {
    const { allContactViewConversations } = _state;
    const currentConversationIndex = allContactViewConversations.findIndex(
      c => c.id === conversation.id
    );
    if (currentConversationIndex > -1) {
      const { messages, ...conversationAttributes } = conversation;
      const existingConversation = allContactViewConversations[currentConversationIndex];

      // Preserve existing sender labels if not provided in the update
      const existingLabels = existingConversation.meta?.sender?.labels || [];
      const newSenderLabels = conversationAttributes.meta?.sender?.labels;

      const currentConversation = {
        ...existingConversation,
        ...conversationAttributes,
      };

      // If the update doesn't include labels, preserve the existing ones
      if (currentConversation.meta?.sender && !newSenderLabels) {
        currentConversation.meta.sender.labels = existingLabels;
      }

      Vue.set(allContactViewConversations, currentConversationIndex, currentConversation);
      if (_state.selectedChatId === conversation.id) {
        emitter.emit(BUS_EVENTS.FETCH_LABEL_SUGGESTIONS);
        emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
      }
    }
    // Do not push when not in list: avoids showing a contact from a previous
    // filter (e.g. label A) after switching to another filter (e.g. label B).
  },

  [types.CONTACT_SET_LIST_LOADING_STATUS](_state) {
    _state.listLoadingStatus = true;
  },

  [types.CONTACT_CLEAR_LIST_LOADING_STATUS](_state) {
    _state.listLoadingStatus = false;
  },

  [types.CONTACT_UPDATE_MESSAGE_UNREAD_COUNT](
    _state,
    { id, lastSeen, unreadCount = 0 }
  ) {
    const [chat] = _state.allContactViewConversations.filter(c => c.id === id);
    if (chat) {
      Vue.set(chat, 'agent_last_seen_at', lastSeen);
      Vue.set(chat, 'unread_count', unreadCount);
    }
  },
  [types.CONTACT_CHANGE_CHAT_STATUS_FILTER](_state, data) {
    _state.chatStatusFilter = data;
  },

  [types.CONTACT_CHANGE_CHAT_SORT_FILTER](_state, data) {
    _state.chatSortFilter = data;
  },

  // Update assignee on action cable message
  [types.CONTACT_UPDATE_ASSIGNEE](_state, payload) {
    const [chat] = _state.allContactViewConversations.filter(c => c.id === payload.id);
    Vue.set(chat.meta, 'assignee', payload.assignee);
  },

  [types.CONTACT_UPDATE_CONVERSATION_CONTACT](_state, { conversationId, ...payload }) {
    const [chat] = _state.allContactViewConversations.filter(c => c.id === conversationId);
    if (chat) {
      Vue.set(chat.meta, 'sender', payload);
    }
  },

  [types.CONTACT_UPDATE_SENDER_LABELS](_state, { contactId, labels }) {
    // Update labels in all conversations for this contact
    const numericContactId = Number(contactId);
    _state.allContactViewConversations.forEach(chat => {
      if (chat.meta?.sender?.id === numericContactId) {
        Vue.set(chat.meta.sender, 'labels', labels);
      }
    });
  },

  [types.CONTACT_SET_ACTIVE_INBOX](_state, inboxId) {
    _state.currentInbox = inboxId ? parseInt(inboxId, 10) : null;
  },

  [types.CONTACT_SET_CONVERSATION_CAN_REPLY](_state, { conversationId, canReply }) {
    const [chat] = _state.allContactViewConversations.filter(c => c.id === conversationId);
    if (chat) {
      Vue.set(chat, 'can_reply', canReply);
    }
  },

  [types.CLEAR_CONTACT_CONVERSATIONS](_state, contactId) {
    const chats = _state.allContactViewConversations.filter(
      c => c.meta.sender.id !== contactId
    );
    Vue.set(_state, 'allContactViewConversations', chats);
  },

  [types.CONTACT_SET_CONVERSATION_FILTERS](_state, data) {
    _state.appliedFilters = data;
  },

  [types.CONTACT_CLEAR_CONVERSATION_FILTERS](_state) {
    _state.appliedFilters = [];
  },

  [types.CONTACT_SET_LAST_MESSAGE_ID_IN_SYNC_CONVERSATION](
    _state,
    { conversationId, messageId }
  ) {
    _state.syncConversationsMessages[conversationId] = messageId;
  },

  [types.CONTACT_SET_CONTEXT_MENU_CHAT_ID](_state, chatId) {
    _state.contextMenuChatId = chatId;
  },

  [types.CONTACT_SET_CHAT_LIST_FILTERS](_state, data) {
    _state.conversationFilters = data;
  },
  [types.CONTACT_UPDATE_CHAT_LIST_FILTERS](_state, data) {
    _state.conversationFilters = { ..._state.conversationFilters, ...data };
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
